<template>
    <Dropdown
        placement="bottom-start"
        transfer
        trigger="custom"
        :visible="dropdownIsVisible"
        @onClickoutside="toggleDropdown"
    >
        <hox-dropdown-button
            :is-active="dropdownIsVisible"
            :is-disabled="deliverables.length === 0 || isDisabled"
            :is-loading="isSettingVisibleTo"
            data-testid="library__visibility-select"
            @click="toggleDropdown"
        >
            <slot>Visibility</slot>
        </hox-dropdown-button>

        <template #list>
            <CheckboxGroup v-model="visibleToRoleIds">
                <DropdownItem v-for="{ label, value } in options" :key="value">
                    <Checkbox :label="value" :data-testid="'library__visibility-select--' + value">
                        {{ label }}
                    </Checkbox>
                </DropdownItem>
            </CheckboxGroup>
            <div class="set-deliverables-visible-to-dropdown__dropdown-footer">
                <Button data-testid="library__visibility-button--cancel" @click="toggleDropdown">Cancel</Button>
                <Button type="primary" data-testid="library__visibility-button--save" @click="saveDeliverableVisibleTo">
                    Save
                </Button>
            </div>
        </template>
    </Dropdown>
</template>

<script lang="ts">
import { defineComponent, type PropType } from "vue";
import { mapStores } from "pinia";

import type { ConstructedDeliverable } from "@/types/types";

import HoxDropdownButton from "@/components/common/DropdownButton.vue";
import { setDeliverablesVisibleToRoles } from "@/services/Qa";
import { DeliverableLibraryAction } from "@/store/modules/deliverableLibrary";
import { RolesGetter, type Role } from "@/store/modules/roles";
import { useCreativeLibraryFiltersStore } from "@/store/pinia/creativeLibraryFilters";

export default defineComponent({
    components: {
        HoxDropdownButton
    },
    props: {
        deliverables: {
            required: true,
            type: Array as PropType<ConstructedDeliverable[]>
        },
        isDisabled: {
            type: Boolean
        },
        itemToChangeNumber: {
            required: false,
            type: Number
        }
    },
    data() {
        return {
            dropdownIsVisible: false,
            isSettingVisibleTo: false,
            visibleToRoleIds: []
        };
    },
    computed: {
        ...mapStores(useCreativeLibraryFiltersStore),
        campaignId() {
            return this.$route.params.campaignId as string;
        },
        options() {
            return this.rolesThatCanOnlyViewDeliverablesVisibleToRole.map(role => ({
                label: role.name,
                value: role._id
            }));
        },
        rolesThatCanOnlyViewDeliverablesVisibleToRole(): Role[] {
            return this.$store.getters[RolesGetter.getRolesThatDoNotHaveAction](
                this.$auth.Actions.CanChangeDeliverableVisibility
            );
        }
    },
    methods: {
        async saveDeliverableVisibleTo() {
            /*
                We make a copy of the current visibleToRoleIds because when we
                toggle the dropdown we reset the component visibleToRoleIds data.
            */
            const visibleToRoleIds = [...this.visibleToRoleIds];
            this.toggleDropdown();
            this.isSettingVisibleTo = true;
            const deliverablesCount = this.itemToChangeNumber || this.deliverables.length;
            try {
                const deliverables = await setDeliverablesVisibleToRoles(
                    this.campaignId,
                    this.deliverables,
                    visibleToRoleIds,
                    this.creativeLibraryFiltersStore.getComputedLibraryFilters
                );
                this.$store.dispatch(DeliverableLibraryAction.SetDeliverableStatusByIdHash, deliverables);
                if (visibleToRoleIds.length > 0) {
                    const roleNamesNowVisibleTo = visibleToRoleIds.map(roleId => {
                        const option = this.options.find(option => option.value === roleId);

                        return `${option?.label}s`;
                    });
                    const joinedRoleNamesNowVisibleTo = roleNamesNowVisibleTo.join(", ");
                    if (deliverablesCount === 1) {
                        this.$snackbar.success(`1 deliverable is now visible to ${joinedRoleNamesNowVisibleTo}.`);
                    } else {
                        this.$snackbar.success(
                            `${deliverablesCount} deliverables are now visible to ${joinedRoleNamesNowVisibleTo}.`
                        );
                    }
                } else if (deliverablesCount === 1) {
                    this.$snackbar.success(
                        "1 deliverable is now only visible to users who can always see all deliverables."
                    );
                } else {
                    this.$snackbar.success(
                        `${deliverablesCount} deliverables are now only visible to users who can always see all deliverables.`
                    );
                }
            } catch {
                this.$snackbar.error(
                    "There was an unexpected error and the deliverable visibility was unable to be set. Please try again in a few moments."
                );
            }
            this.isSettingVisibleTo = false;
        },
        toggleDropdown() {
            this.dropdownIsVisible = !this.dropdownIsVisible;
            if (!this.dropdownIsVisible) {
                this.visibleToRoleIds = [];
            }
        }
    }
});
</script>

<style lang="scss">
@use "@style/variables" as *;

.set-deliverables-visible-to-dropdown__dropdown-footer {
    display: flex;
    justify-content: flex-end;
    padding: $spacing $spacing-small ($spacing-small - 5px) $spacing-large;
    gap: 8px;
}
</style>
